import type { GrepOptions, SearchType } from "./searchRequest";

export type GrepSearchPanelState = {
  query: string;
  grepOptions: GrepOptions;
  searchId: string | null;
};

export type LlmSearchPanelState = {
  query: string;
  model: string;
  searchId: string | null;
};

export type SearchPanelState = {
  searchType: SearchType;
  searches: {
    grep: GrepSearchPanelState;
    llm: LlmSearchPanelState;
  };
};

type StoredGrepSearchPanelState = Partial<
  Omit<GrepSearchPanelState, "grepOptions">
> & {
  grepOptions?: Partial<GrepOptions>;
};

export type StoredSearchPanelState = {
  searchType?: SearchType;
  searches?: {
    grep?: StoredGrepSearchPanelState;
    llm?: Partial<LlmSearchPanelState>;
  };
};

export const createInitialSearchPanelState = (): SearchPanelState => ({
  searchType: "llm",
  searches: {
    grep: {
      query: "",
      searchId: null,
      grepOptions: {
        ignoreCase: true,
        regex: false,
        wordBoundary: false,
      },
    },
    llm: {
      query: "",
      searchId: null,
      model: "",
    },
  },
});

// Switching search type carries an unsubmitted draft over to the tab you switch
// to (and leaves the original empty), so typing in the wrong mode isn't lost.
// Anything already typed in the target, or a query that has been submitted
// (searchId set), is left untouched.
export const applySearchTypeChange = (
  prev: SearchPanelState,
  type: SearchType
): SearchPanelState => {
  if (type === prev.searchType) return prev;

  const source = prev.searches[prev.searchType];
  const target = prev.searches[type];
  const sourceIsDraft = source.searchId === null && source.query.trim() !== "";
  const targetIsEmpty = target.query === "" && target.searchId === null;

  if (!sourceIsDraft || !targetIsEmpty) {
    return { ...prev, searchType: type };
  }

  if (type === "llm") {
    return {
      ...prev,
      searchType: "llm",
      searches: {
        ...prev.searches,
        grep: { ...prev.searches.grep, query: "" },
        llm: { ...prev.searches.llm, query: source.query },
      },
    };
  }

  return {
    ...prev,
    searchType: "grep",
    searches: {
      ...prev.searches,
      grep: { ...prev.searches.grep, query: source.query },
      llm: { ...prev.searches.llm, query: "" },
    },
  };
};

const mergeGrepOptions = (options?: Partial<GrepOptions>): GrepOptions => ({
  ignoreCase: options?.ignoreCase ?? true,
  regex: options?.regex ?? false,
  wordBoundary: options?.wordBoundary ?? false,
});

export const normalizeSearchPanelState = (
  state?: StoredSearchPanelState
): SearchPanelState => {
  const initial = createInitialSearchPanelState();
  if (!state) return initial;

  return {
    searchType: state.searchType ?? initial.searchType,
    searches: {
      grep: {
        query: state.searches?.grep?.query ?? initial.searches.grep.query,
        searchId:
          state.searches?.grep?.searchId ?? initial.searches.grep.searchId,
        grepOptions: mergeGrepOptions(state.searches?.grep?.grepOptions),
      },
      llm: {
        query: state.searches?.llm?.query ?? initial.searches.llm.query,
        model: state.searches?.llm?.model ?? initial.searches.llm.model,
        searchId:
          state.searches?.llm?.searchId ?? initial.searches.llm.searchId,
      },
    },
  };
};
